import styles from "./TransactionsMenu.module.scss";
import {
  TRANSACTION_ACTION_ADD_TAG,
  TRANSACTION_ACTION_BOOK_NOW,
  TRANSACTION_ACTION_DELETE,
  TRANSACTION_ACTION_REMOVE_TAG,
  TRANSACTION_ACTION_SET_CATEGORY,
  TRANSACTION_ACTION_SET_TRANSFER,
  TRANSACTION_TYPE_DEFAULT
} from "../../../codes";
import clsx from "clsx";
import {
  addTagsModalSelector,
  chooseCategoryModalSelector,
  listConfigSelector,
  removeTagsModalSelector,
  removeTransactionsModalSelector,
  rxShowAddTagsModal,
  rxShowChooseCategoryModal,
  rxShowRemoveTagsModal,
  rxShowRemoveTransactionsModal,
  rxShowTransfertAssociationModal,
  rxToggleAllTransactions,
  rxToggleInactiveAccounts,
  rxUpdateTransactions,
  transfertAssociationModalSelector
} from "../../../slices/transactionsSlice";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {
  HeaderMenu,
  HeaderMenuActionDropdown,
  HeaderMenuActionDropdownItem,
  HeaderMenuLeft
} from "../../../common/HeaderMenu";
import Modal from "react-bootstrap/Modal";
import Button from "../../../common/Button";
import {selectAllCategories} from "../../../slices/categoriesSlice";
import {TransactionCategory} from "../../../common/TransactionCategory";
import {sendTransactionAction} from "../../../api/ApiTransactions";
import {FormControlTags} from "../../../common/FormControlTags";
import Form from "react-bootstrap/Form";
import useTasker from "../../../util/useTasker";
import {DEFAULT_COLOR} from "../../../common/ColorBox";
import useProject from "../../../util/useProject";
import ClickAwayListener from "react-click-away-listener";
import {selectAllAccounts} from "../../../slices/accountsSlice";

export const TransactionsMenu = ({transactions}) => {
  const dispatch = useDispatch();

  const {t} = useTranslation("transactions");

  const {runWithExpiration} = useProject();

  const [showMoreActions, setShowMoreActions] = useState(false);

  const showMoreRef = useRef();

  const {selectedTransactions, inactiveAccounts} = useSelector(listConfigSelector);
  const accounts = useSelector(selectAllAccounts);

  const selectedTransactionsIds = Object.keys(selectedTransactions);

  const toggleInactiveAccounts = () => {
    dispatch(rxToggleInactiveAccounts());
  };

  const showLinkConfirmation = () => {
    let isValidForlinLinking = selectedTransactionsIds.length === 2;

    if (isValidForlinLinking) {
      const transaction1 = transactions.find(t => t.id === selectedTransactionsIds[0]);
      const transaction2 = transactions.find(t => t.id === selectedTransactionsIds[1]);

      if (transaction1.type !== TRANSACTION_TYPE_DEFAULT
        || transaction2.type !== TRANSACTION_TYPE_DEFAULT
        || transaction1.amount + transaction2.amount !== 0
        || transaction1.account === transaction2.account) {
        isValidForlinLinking = false;
      }
    }

    if (isValidForlinLinking) {
      dispatch(rxShowTransfertAssociationModal({show: true}));
    } else {
      dispatch(rxShowTransfertAssociationModal({show: true, impossible: true}));
    }
  };

  const nbSelectedTransactions = selectedTransactionsIds.length;
  const hasOnlyFutureTransactions = selectedTransactionsIds
    .map(id => transactions.find(t => t.id === id))
    .filter(t => !!t)
    .every(t => t.future);

  useEffect(() => {
    if (nbSelectedTransactions === 0 && showMoreActions) {
      setShowMoreActions(false);
    }
  }, [nbSelectedTransactions, showMoreActions]);

  return <>
    <HeaderMenu title={t("header_title_transactions")}>
      {accounts.some(a => !a.active) && <HeaderMenuLeft>
        <HeaderMenuActionDropdown label={t("menu_display")}>
          <HeaderMenuActionDropdownItem
            icon={inactiveAccounts ? "check_box" : "check_box_outline_blank"}
            onClick={toggleInactiveAccounts} enabled
            label={t("menu_show_inactive_accounts")}
          />
        </HeaderMenuActionDropdown>
      </HeaderMenuLeft>}

      <ChooseCategoryModal/>
      <AddTagsModal/>
      <RemoveTagsModal/>
      <RemoveTransactionsModal/>
      <TransfertAssociationModal/>
    </HeaderMenu>

    <div
      className={clsx(styles.actionsBar, nbSelectedTransactions > 0 ? styles.show : null)}>
      <button className={clsx(styles.action, styles.transactionsCount)}
              onClick={() => dispatch(rxToggleAllTransactions({selectedTransactions: {}}))}>
        <span className="material-icons">close</span>

        {t("menu_selected_transactions_count", {count: nbSelectedTransactions})}
      </button>

      <div className={clsx(styles.divider, styles.dividerCategory)}/>

      <button className={clsx(styles.action, styles.actionCategory)}
              onClick={runWithExpiration(() => dispatch(rxShowChooseCategoryModal(true)))}>
        <span className="material-icons">category</span>

        {t("menu_tooltip_apply_category")}
      </button>

      <div className={clsx(styles.divider, styles.dividerDelete)}/>

      <button className={clsx(styles.action, styles.actionDelete)}
              onClick={runWithExpiration(() => dispatch(rxShowRemoveTransactionsModal(true)))}>
        <span className="material-icons">delete</span>

        {t("menu_tooltip_remove")}
      </button>

      <div className={styles.divider}/>

      <button
        ref={showMoreRef}
        className={clsx(styles.action, showMoreActions ? styles.active : null)}
        onClick={e => {
          e.stopPropagation();

          setShowMoreActions(prev => !prev);
        }}
      >
        <span className="material-icons">more_horiz</span>

        {t("menu_selected_transactions_more")}
      </button>

      <ClickAwayListener onClickAway={e => {
        if (e.target === showMoreRef.current) {
          return;
        }

        setShowMoreActions(false);
      }}>
        <div className={clsx(styles.actionsBarMore, showMoreActions ? styles.active : null)}>
          <ActionMore
            label="menu_tooltip_apply_category"
            icon="category"
            onClick={runWithExpiration(() => dispatch(rxShowChooseCategoryModal(true)))}
            className={styles.actionCategoryMore}
          />

          <ActionMore
            label="menu_tooltip_apply_tag"
            icon="label"
            onClick={runWithExpiration(() => dispatch(rxShowAddTagsModal(true)))}
          />

          <ActionMore
            label="menu_tooltip_remove_tag"
            icon="label_off"
            onClick={runWithExpiration(() => dispatch(rxShowRemoveTagsModal(true)))}
          />

          <ActionMore
            label="menu_tooltip_link_transfer"
            icon="link"
            disabled={nbSelectedTransactions !== 2}
            onClick={runWithExpiration(showLinkConfirmation)}
          />

          <ActionMore
            label="menu_tooltip_book_now"
            icon="history"
            disabled={!hasOnlyFutureTransactions}
            onClick={() => {
              sendTransactionAction({
                type: TRANSACTION_ACTION_BOOK_NOW,
                ids: selectedTransactionsIds
              });

              setShowMoreActions(false);
            }}
          />

          <ActionMore
            label="menu_tooltip_remove"
            icon="delete"
            onClick={runWithExpiration(() => dispatch(rxShowRemoveTransactionsModal(true)))}
            className={styles.actionDeleteMore}
          />
        </div>
      </ClickAwayListener>
    </div>
  </>;
};

const ChooseCategoryModal = () => {
  const {t} = useTranslation("transactions");

  const dispatch = useDispatch();

  const chooseCategoryModal = useSelector(chooseCategoryModalSelector);
  const transactionCategories = useSelector(selectAllCategories);

  const {selectedTransactions} = useSelector(listConfigSelector);

  const [selectedCategory, setSelectedCategory] = useState(null);

  const hideModal = () => dispatch(rxShowChooseCategoryModal(false));

  const {execute, resetTasker, saving, taskPercentage} = useTasker(
    50,
    ids => sendTransactionAction({
      type: TRANSACTION_ACTION_SET_CATEGORY,
      ids,
      category: selectedCategory.id === noCategory.id ? null : selectedCategory.id
    }).then(result => dispatch(rxUpdateTransactions(result))),
    hideModal
  );

  const noCategory = useMemo(() => ({
    id: "0",
    name: t("category_name_unknown"),
    groupName: "-",
    icon: "help_outline",
    color: DEFAULT_COLOR
  }), [t]);

  const onHide = () => {
    setSelectedCategory(null);

    resetTasker();
  };

  const submitModal = async () => await execute(Object.keys(selectedTransactions));

  const selectCategory = category => () => {
    if (saving) {
      return;
    }

    setSelectedCategory(category);
  };

  return <Modal show={chooseCategoryModal.show} onExited={onHide}>
    <Modal.Header>
      <Modal.Title>
        {t("menu_modal_choose_category_title")}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className={clsx(styles.category, noCategory === selectedCategory ? styles.active : null)}
           onClick={selectCategory(noCategory)}>
        <TransactionCategory category={noCategory}/>
      </div>

      {transactionCategories
        .toSorted((a, b) => (a.groupName + a.name).localeCompare(b.groupName + b.name))
        .map(cat =>
          <div key={cat.id} className={clsx(styles.category, cat === selectedCategory ? styles.active : null)}
               onClick={selectCategory(cat)}>
            <TransactionCategory category={cat}/>
          </div>)}
    </Modal.Body>
    <Modal.Footer>
      <Button onClick={hideModal} variant="secondary"
              disabled={saving}>{t("menu_modal_choose_category_button_cancel")}</Button>
      <Button variant="primary" onClick={submitModal} disabled={saving || !selectedCategory}>
        {t("menu_modal_choose_category_button_apply")}

        {taskPercentage !== null && " (" + taskPercentage.toFixed(0) + "%)"}
      </Button>
    </Modal.Footer>
  </Modal>;
};

const AddTagsModal = () => {
  const {t} = useTranslation("transactions");

  const dispatch = useDispatch();

  const addTagsModal = useSelector(addTagsModalSelector);

  const {selectedTransactions} = useSelector(listConfigSelector);

  const [tags, setTags] = useState("");

  const hideModal = () => dispatch(rxShowAddTagsModal(false));

  const {execute, resetTasker, saving, taskPercentage} = useTasker(
    50,
    ids => sendTransactionAction({
      type: TRANSACTION_ACTION_ADD_TAG,
      ids,
      tags: tags.split(" ").map(tag => tag.trim()).filter(tag => tag.length > 0)
    }).then(result => dispatch(rxUpdateTransactions(result))),
    hideModal
  );

  const onHide = () => {
    setTags("");

    resetTasker();
  };

  const submitModal = async e => {
    e.preventDefault();

    await execute(Object.keys(selectedTransactions));
  };

  return <Modal show={addTagsModal.show} onExited={onHide}>
    <Form onSubmit={submitModal}>
      <Modal.Header>
        <Modal.Title>
          {t("menu_modal_add_tags_title")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormControlTags value={tags} onChange={e => setTags(e.target.value)} autoFocus
                         placeholder={t("menu_modal_add_tags_placeholder")} disabled={saving}/>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={hideModal} disabled={saving}
                variant="secondary">{t("menu_modal_add_tags_button_cancel")}</Button>
        <Button type="submit" variant="primary" disabled={saving || tags.trim().length === 0}>
          {t("menu_modal_add_tags_button_apply")}

          {taskPercentage !== null && " (" + taskPercentage.toFixed(0) + "%)"}
        </Button>
      </Modal.Footer>
    </Form>
  </Modal>;
};

const RemoveTagsModal = () => {
  const {t} = useTranslation("transactions");

  const dispatch = useDispatch();

  const removeTagsModal = useSelector(removeTagsModalSelector);

  const {selectedTransactions} = useSelector(listConfigSelector);

  const [tags, setTags] = useState("");

  const hideModal = () => dispatch(rxShowRemoveTagsModal(false));

  const {execute, resetTasker, saving, taskPercentage} = useTasker(
    50,
    ids => sendTransactionAction({
      type: TRANSACTION_ACTION_REMOVE_TAG,
      ids,
      tags: tags.split(" ").map(tag => tag.trim()).filter(tag => tag.length > 0)
    }).then(result => dispatch(rxUpdateTransactions(result))),
    hideModal
  );

  const onHide = () => {
    setTags("");

    resetTasker();
  };

  const submitModal = async e => {
    e.preventDefault();

    await execute(Object.keys(selectedTransactions));
  };

  return <Modal show={removeTagsModal.show} onExited={onHide}>
    <Form onSubmit={submitModal}>
      <Modal.Header>
        <Modal.Title>
          {t("menu_modal_remove_tags_title")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormControlTags value={tags} onChange={e => setTags(e.target.value)} autoFocus
                         placeholder={t("menu_modal_remove_tags_placeholder")} disabled={saving}/>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={hideModal} variant="secondary"
                disabled={saving}>{t("menu_modal_remove_tags_button_cancel")}</Button>
        <Button type="submit" variant="danger" disabled={saving || tags.trim().length === 0}>
          {t("menu_modal_remove_tags_button_apply")}

          {taskPercentage !== null && " (" + taskPercentage.toFixed(0) + "%)"}
        </Button>
      </Modal.Footer>
    </Form>
  </Modal>;
};

const RemoveTransactionsModal = () => {
  const {t} = useTranslation("transactions");

  const dispatch = useDispatch();

  const removeTransactionsModal = useSelector(removeTransactionsModalSelector);

  const {selectedTransactions} = useSelector(listConfigSelector);

  const hideModal = () => dispatch(rxShowRemoveTransactionsModal(false));

  const {execute, resetTasker, saving, taskPercentage} = useTasker(
    50,
    ids => sendTransactionAction({type: TRANSACTION_ACTION_DELETE, ids})
      .then(result => dispatch(rxUpdateTransactions(result))),
    hideModal
  );

  const onHide = () => {
    resetTasker();
  };

  const submitModal = async e => {
    e.preventDefault();

    await execute(Object.keys(selectedTransactions));
  };

  return <Modal show={removeTransactionsModal.show} onExited={onHide}>
    <Form onSubmit={submitModal}>
      <Modal.Header>
        <Modal.Title>{t("menu_modal_remove_title", {count: Object.keys(selectedTransactions).length})}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{t("menu_modal_remove_text", {count: Object.keys(selectedTransactions).length})}</Modal.Body>
      <Modal.Footer>
        <Button onClick={hideModal} variant="secondary"
                disabled={saving}>{t("menu_modal_remove_button_cancel")}</Button>
        <Button type="submit" variant="danger" disabled={saving}>
          {t("menu_modal_remove_button_apply")}

          {taskPercentage !== null && " (" + taskPercentage.toFixed(0) + "%)"}
        </Button>
      </Modal.Footer>
    </Form>
  </Modal>;
};

const TransfertAssociationModal = () => {
  const {t} = useTranslation("transactions");

  const dispatch = useDispatch();

  const transfertAssociationModal = useSelector(transfertAssociationModalSelector);

  const {selectedTransactions} = useSelector(listConfigSelector);

  const hideModal = () => dispatch(rxShowTransfertAssociationModal({}));

  const {execute, resetTasker, saving, taskPercentage} = useTasker(
    50,
    ids => sendTransactionAction({type: TRANSACTION_ACTION_SET_TRANSFER, ids: ids})
      .then(result => dispatch(rxUpdateTransactions(result))),
    hideModal
  );

  const onHide = () => {
    resetTasker();
  };

  const submitModal = async e => {
    e.preventDefault();

    await execute(Object.keys(selectedTransactions));
  };

  const title = transfertAssociationModal.impossible ? "menu_modal_linking_title_impossible" : "menu_modal_linking_title";
  const body = transfertAssociationModal.impossible ? "menu_modal_linking_choose_same_transactions" : "menu_modal_linking_confirmation_text";

  return <Modal show={transfertAssociationModal.show} onExited={onHide}>
    <Form onSubmit={submitModal}>
      <Modal.Header>
        <Modal.Title>{t(title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{t(body)}</Modal.Body>
      <Modal.Footer>
        <Button
          onClick={hideModal}
          variant="secondary"
          disabled={saving}
        >{t("menu_modal_linking_button_cancel")}</Button>

        {!transfertAssociationModal.impossible && <Button type="submit" variant="primary" disabled={saving}>
          {t("menu_modal_linking_button_confirm")}

          {taskPercentage !== null && " (" + taskPercentage.toFixed(0) + "%)"}
        </Button>}
      </Modal.Footer>
    </Form>
  </Modal>;
};

const ActionMore = ({label, icon, onClick, className = null, disabled = false}) => {
  const {t} = useTranslation("transactions");

  return <button className={clsx(styles.actionMore, disabled ? styles.disabled : null, className)} onClick={() => {
    if (!disabled) {
      onClick();
    }
  }}>
    <span className="material-icons">{icon}</span>

    {t(label)}
  </button>;
};
